import React from 'react';
import { Icon } from '../Icon';
import styles from './TablePagination.module.css';

/**
 * Compact floating pagination pill for use with DataTable.
 *
 * Renders as a small inline-flex pill anchored to the bottom-right of the
 * table via the paginationSlot in DataTable. All controls live in one row:
 *   [ Show: 10 ▾ ] | [|◀] [◀]  Page X of Y  [▶] [▶|]
 *
 * Props:
 *   currentPage          — 1-based current page number
 *   totalItems           — total number of rows across all pages
 *   itemsPerPage         — current page size
 *   pageSizeOptions      — array of selectable page sizes (default [5, 10, 20, 50])
 *   onPageChange         — (page) => void
 *   onItemsPerPageChange — (size) => void; consumer should also reset currentPage to 1
 *   itemLabel            — unused (kept for API compatibility)
 */
const TablePagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  pageSizeOptions = [5, 10, 20, 50],
  onPageChange,
  onItemsPerPageChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  if (totalItems === 0) return null;

  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const goTo = (page) => {
    if (page >= 1 && page <= totalPages && page !== safePage) {
      onPageChange?.(page);
    }
  };

  return (
    <div className={styles.pagination}>
      {/* Items per page selector */}
      <div className={styles.pageSizeWrap}>
        <label className={styles.pageSizeLabel} htmlFor="pageSize">
          Show:
        </label>
        <select
          id="pageSize"
          className={styles.pageSizeSelect}
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange?.(Number(e.target.value))}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      <span className={styles.divider} aria-hidden="true" />

      {/* First page */}
      <button
        type="button"
        className={styles.navButton}
        onClick={() => goTo(1)}
        disabled={safePage === 1}
        aria-label="First page"
      >
        <Icon name="chevrons-left" />
      </button>

      {/* Previous page */}
      <button
        type="button"
        className={styles.navButton}
        onClick={() => goTo(safePage - 1)}
        disabled={safePage === 1}
        aria-label="Previous page"
      >
        <Icon name="chevron-left" />
      </button>

      {/* Page indicator */}
      <span className={styles.pageIndicator}>
        Page {safePage} of {totalPages}
      </span>

      {/* Next page */}
      <button
        type="button"
        className={styles.navButton}
        onClick={() => goTo(safePage + 1)}
        disabled={safePage === totalPages}
        aria-label="Next page"
      >
        <Icon name="chevron-right" />
      </button>

      {/* Last page */}
      <button
        type="button"
        className={styles.navButton}
        onClick={() => goTo(totalPages)}
        disabled={safePage === totalPages}
        aria-label="Last page"
      >
        <Icon name="chevrons-right" />
      </button>
    </div>
  );
};

export default TablePagination;
